import { UserContext } from './Auth';
import { createContext, useContext, useState, useEffect } from 'react';
import LoadingVisualization from './LoadingVisualization';

export const DataContext = createContext();

export default function Data({ children }) {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    users: [],
    stores: [],
    companies: [],
    hours: [],
    employees: [],
    schedules: [],
    shifts: [],
    availabilities: [],
    sales: [],
    weather: [],
    usergroups: [],
    users2groups: [],
    // inventory data removed - now loaded lazily in inventory components
  });
  const [loadingStatus, setLoadingStatus] = useState({});

  // API paths for fetching data
  const apiPaths = [
    { path: '/api/users/', key: 'users' },
    { path: '/api/usergroups/', key: 'usergroups' },
    { path: '/api/users2groups/', key: 'users2groups' },
    { path: '/api/stores/', key: 'stores' },
    { path: '/api/companies/', key: 'companies' },
    { path: '/api/hours/', key: 'hours' },
    { path: '/api/employees/', key: 'employees' },
    // inventory APIs removed - now loaded lazily in inventory components
    // Uncomment these as needed:
    // { path: '/api/schedules/', key: 'schedules' },
    // { path: '/api/shifts/', key: 'shifts' },
    // { path: '/api/availabilities/', key: 'availabilities' },
    // { path: '/api/hourlysales', key: 'sales' },
    // { path: '/api/weatherdays', key: 'weather' },
  ];

  // Helper function to filter data for specific date ranges and stores
  const mkWindow = (
    allData,
    store,
    dStart = new Date(new Date().setDate(new Date().getDate() - 7)), // Default: 7 days ago
    dEnd = new Date() // Default: today
  ) => {
    let filteredData = {};

    if (!store) return filteredData;

    console.log('Creating data window for store:', store);

    // Filter data per logic
    filteredData.sales = allData.sales
      .filter((row) => row.store_id === store)
      .filter((row) => {
        const date = new Date(row.day);
        return date >= dStart && date <= dEnd;
      });

    filteredData.employees = allData.employees.filter(
      (row) => row.store_id === store
    );

    const employeeWindow = new Set(filteredData.employees.map((row) => row.source_id));

    filteredData.shifts = allData.shifts
      .filter((row) => employeeWindow.has(row.employee_id))
      .filter((row) => {
        const date = new Date(row.day);
        return date >= dStart && date <= dEnd;
      });

    filteredData.availabilities = allData.availabilities.reduce((filtered, row) => {
      if (!employeeWindow.has(row.employee_id)) return filtered;

      row.effective = new Date(row.effective);
      if (
        row.employee_id in filtered &&
        row.effective < filtered[row.employee_id].effective
      ) {
        return filtered;
      } else {
        filtered[row.employee_id] = { ...row };
      }
      return filtered;
    }, {});

    filteredData.timepunches = allData.timepunches
      .filter((row) => employeeWindow.has(row.employee_id))
      .filter((row) => {
        const date = new Date(row.date);
        return date >= dStart && date <= dEnd;
      });

    return filteredData;
  };

  // Fetch data from APIs
  useEffect(() => {
    console.log('Data component useEffect - user:', user);
    console.log('Data component useEffect - current path:', window.location.pathname);
    
    // Skip data loading for public routes and marketing pages
    const publicRoutes = ["/demo/", "/login", "/blog"];
    const currentPath = window.location.pathname;
    const isPublicRoute = publicRoutes.some(route => {
      // For routes, use startsWith for sub-paths
      const matches = currentPath === route || currentPath.startsWith(route);
      if (matches) {
        console.log('Data component - route matched:', route, 'for path:', currentPath);
      }
      return matches;
    });
    
    console.log('Data component useEffect - isPublicRoute:', isPublicRoute);
    
    if (!user || isPublicRoute) {
      console.log('Data component useEffect - skipping data load, user:', !!user, 'isPublicRoute:', isPublicRoute);
      setLoading(false);
      return;
    }
    
    console.log('Data component useEffect - starting data load');

    const loadData = async () => {
      try {
        setLoading(true);

        // Helper function to fetch data for individual API endpoints
        const fetchData = async (path) => {
          const response = await fetch(path, {
            credentials: 'include' // Include authentication cookies
          });
          if (!response.ok) {
            console.error(`Failed fetching ${path}:`, response.status);
            return [];
          }
          return response.json();
        };

        // Map API endpoints to fetch requests
        const promises = apiPaths.map(async ({ path, key }) => {
          setLoadingStatus((prev) => ({ ...prev, [key]: 'Loading...' }));
          const result = await fetchData(path);
          const recordCount = Array.isArray(result) ? result.length : 1; // Assume a single object or array
          setLoadingStatus((prev) => ({ ...prev, [key]: `${recordCount} records` }));
          return { key, result };
        });

        // Resolve all fetch operations
        const results = await Promise.all(promises);

        // Combine results into a single data object
        const newData = results.reduce(
          (acc, { key, result }) => ({
            ...acc,
            [key]: Array.isArray(result) ? result : [],
          }),
          {}
        );
        
        console.log('Data component - loaded data:', {
          companies: newData.companies?.length || 0,
          stores: newData.stores?.length || 0,
          users: newData.users?.length || 0
        });

        // Derive additional fields
        const companiesInUse = {};
        (newData.stores || []).forEach((store) => {
          companiesInUse[store.company_id] = true;
        });

        newData.weekStarts = (newData.companies || []).reduce((weekStartMap, company) => {
          if (companiesInUse[company.source_id]) {
            weekStartMap[company.source_id] = company.week_begin;
          }
          return weekStartMap;
        }, {});

        mkWindow(newData); // Create initial filtered data window

        setData(newData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Render loading indicators if still fetching data
  if (loading) {
    return <LoadingVisualization apiPaths={apiPaths} loadingStatus={loadingStatus} />;
  }

  return (
    <DataContext.Provider value={{ data, setData, mkWindow }}>
      {children}
    </DataContext.Provider>
  );
}
